/**
 * Copy + URL-param parsing for `/signup`.
 *
 * Deliberately its OWN module with NO `"use client"` directive: `/signup` is a
 * server component and reads `searchParams` there. A plain function exported
 * from a client module is a client *reference*, not a callable — calling one
 * during SSR fails with "a is not a function" — so the pure helpers live here
 * and everything that needs the browser lives in `signup-model`.
 *
 * Every string below is verbatim from the live reference (xgodo.com/signup). UX
 * parity is strict: exactly these nine fields, two checkboxes and one button —
 * nothing added (no "confirm email", no password-strength meter, no social
 * buttons) and nothing dropped (the captcha in particular stays).
 */

/* --- Copy (verbatim from the live page) ------------------------------------ */

export const SIGNUP_NOTICE =
  "NOTE: One account per computer, our system detects the IP of users and if more than one account is created you will be banned and unable to join this site again.";

export const SIGNUP_TITLE = "Create an Account";
export const SIGNUP_HAVE_ACCOUNT = "Already have an account?";
export const SIGNUP_LOGIN_LINK = "Log In";

export const SIGNUP_TERMS_LABEL =
  "By signing up for an account you agree to our Terms and Conditions";
export const SIGNUP_NEWSLETTER_LABEL = "I wish to receive newsletter";

export const SIGNUP_SUBMIT = "Sign up";
export const SIGNUP_SUBMITTING = "Signing up…";

/** Field labels, live-verbatim (including the lowercase "phone number"). */
export const SIGNUP_LABELS = {
  fname: "First Name",
  lname: "Last Name",
  email: "Email",
  username: "Username",
  phone: "phone number",
  password: "Password",
  confirmpassword: "Confirm Password",
  country_id: "Select your country",
  captcha: "Security Code",
} as const;

/** The country <select>'s resting option — the live page's placeholder. */
export const SIGNUP_COUNTRY_PLACEHOLDER = "Select your country";

/* --- URL params ------------------------------------------------------------ */

/**
 * `?scenario=` → a forced display state, so every state is reviewable without
 * driving the backend:
 *   - `empty`      blank fields (the default, and the real first paint)
 *   - `populated`  every field carrying a value, as if typed
 *   - `loading`    mid-submit: inputs locked, progress treatment, no spinner
 *   - `error`      the failed-submit surface: the alert plus the two inline
 *                  errors it names (password mismatch + wrong security code)
 *
 * Kept after the bake-off closed, as `/labelling` and `/admin/login` kept theirs:
 * it costs nothing and makes every display state reviewable from the URL.
 */
export type SignupScenario = "populated" | "empty" | "loading" | "error";

export function parseSignupScenario(
  raw: string | string[] | undefined,
): SignupScenario {
  const value = Array.isArray(raw) ? raw[0] : raw;
  switch (value) {
    case "populated":
    case "loading":
    case "error":
      return value;
    default:
      return "empty";
  }
}
